/* eslint-disable require-atomic-updates */
import { useCallback, useEffect, useRef } from 'react'
import { useLazyQuery, useReactiveVar } from '@apollo/client'
import {
  GetDemoBullseyeQuotaQuery,
  GetDemoUpDownAiQuotaQuery,
  GetGuestSessionQuery
} from '__generated__/graphql'
import { GET_GUEST_SESSION } from 'api/guest-session/get-guest-session'
import { GET_DEMO_BULLSEYE_QUOTA } from 'api/mode-bulls-eye/get-demo-bullseye-quota'
import { GET_DEMO_UP_DOWN_AI_QUOTA } from 'api/mode-up-down-ai-demo/get-demo-up-down-ai-quota'
import { isGuestClaimDialogOpenVar } from 'shared/store/dialogs'
import { userVar } from 'shared/store/user'
import {
  EMPTY_GUEST_DEMO_ROUNDS,
  guestDemoRoundsVar,
  type GuestDemoRounds
} from '../store'
import { countRounds } from '../utils/describe-claim'

const LAZY = { fetchPolicy: 'no-cache' } as const

const readRounds = (
  upDownAi?: GetDemoUpDownAiQuotaQuery['getDemoUpDownAiQuota'] | null,
  bullseye?: GetDemoBullseyeQuotaQuery['getDemoBullseyeQuota'] | null
): GuestDemoRounds => ({
  upDownAi: upDownAi?.roundsPlayed ?? 0,
  bullseye: bullseye?.roundsPlayed ?? 0
})

const isClaimable = (
  session: GetGuestSessionQuery['getGuestSession'],
  rounds: GuestDemoRounds
) => Boolean(session?.id) && !session?.claimedAt && countRounds(rounds) > 0

/**
 * Offers the demo-results claim once per signed-in user, whatever the way
 * in (wallet, Privy email / OAuth / wallet): the guest session lives in a
 * cookie on the API host, so it survives the OAuth redirect and a reload,
 * and the check simply re-runs when `userVar` is set again (FE-310).
 */
export const useMergeGuestSession = () => {
  const user = useReactiveVar(userVar)
  const promptedForUserRef = useRef<string | null>(null)
  const [getGuestSession] = useLazyQuery(GET_GUEST_SESSION, LAZY)
  const [getUpDownAiQuota] = useLazyQuery(GET_DEMO_UP_DOWN_AI_QUOTA, LAZY)
  const [getBullseyeQuota] = useLazyQuery(GET_DEMO_BULLSEYE_QUOTA, LAZY)

  const promptIfNeeded = useCallback(async () => {
    if (!user?.id || promptedForUserRef.current === user.id) return
    promptedForUserRef.current = user.id

    const [session, upDownAi, bullseye] = await Promise.all([
      getGuestSession(),
      getUpDownAiQuota(),
      getBullseyeQuota()
    ])
    const rounds = readRounds(
      upDownAi.data?.getDemoUpDownAiQuota,
      bullseye.data?.getDemoBullseyeQuota
    )
    if (!isClaimable(session.data?.getGuestSession, rounds)) return

    guestDemoRoundsVar(rounds)
    isGuestClaimDialogOpenVar(true)
  }, [getBullseyeQuota, getGuestSession, getUpDownAiQuota, user?.id])

  useEffect(() => {
    if (!user?.id) {
      promptedForUserRef.current = null
      guestDemoRoundsVar(EMPTY_GUEST_DEMO_ROUNDS)
      isGuestClaimDialogOpenVar(false)
      return
    }

    void promptIfNeeded()
  }, [promptIfNeeded, user?.id])
}
